import { STAGE, offerStage } from './stage.js';
import { CODE_WORLD_TEXTS, WORLDS } from './world-data.js';
import { RESERVED_WORLD_SLUGS, packageExtras } from './world-package/format.js';
import { decodeGround, decodeSight, } from './world-package/measure.js';
import { movingFeatures } from './world-package/design-rules.js';
import { hasAliases, orderedFeatures, orderedStates, packageAliases, } from './world-package/order.js';
import { packageScenery } from './world-package/scenery.js';
import { packageSight } from './world-package/sight.js';
import { CODE_WORLD_SIGHT } from './world-sight.generated.js';
/** The code worlds, in WORLDS' order, with their texts. */
export const CODE_WORLD_CATALOGUE = WORLDS.map((entry) => {
    const texts = CODE_WORLD_TEXTS[entry.slug];
    const staged = entry.staged !== false;
    return {
        slug: entry.slug,
        name: { nl: entry.name, en: texts.nameEn },
        description: texts.description,
        teacher: texts.teacher,
        source: 'code',
        staged,
        placement: entry.slug === 'villa',
        generatable: staged,
        features: entry.features,
        picture: `plaatsen/${entry.slug}.jpg`,
        ...codeWorldSight(entry.slug),
    };
});
/** A code world's sight and fog (world-sight.generated.ts), when it was measured. */
export function codeWorldSight(slug, measured = CODE_WORLD_SIGHT) {
    const known = measured[slug];
    if (known === undefined)
        return {};
    return {
        sight: decodeSight(known.sight),
        sightComplete: true,
        ...(known.ground === null ? {} : { ground: decodeGround(known.ground) }),
        ...(known.fog === null ? {} : { fog: known.fog }),
    };
}
/**
 * A checked package as a catalogue world; `picture` is its signed URL (or a captured one), or null.
 * `measured`: what the media worker measured of its files, for its sight profile (ADR-053); without
 * it, only its props count; its `ground` (the API's, from the height map) stands models on the land.
 */
export function catalogueWorldOfPackage(pkg, picture, measured, 
/** Its old names when they travel beside the package (a served world's); else the package's own. */
aliases = packageAliases(pkg)) {
    const extras = packageExtras(pkg);
    const { sight, complete } = packageSight(pkg, measured);
    const placement = pkg.blocks.some((block) => block.type === 'plaatsruimte');
    const movers = movingFeatures(pkg);
    return {
        slug: pkg.slug,
        name: { nl: pkg.name.nl, en: pkg.name.en },
        description: extras.descriptionEn,
        teacher: { title: pkg.name.nl, text: pkg.description },
        source: 'pakket',
        staged: extras.staged,
        placement,
        // Placement-only for now (ADR-050): a world for placing work is never in a generated lesson.
        generatable: extras.staged && !placement,
        // In the maker's order (order.ts): every list of features and states reads this one.
        features: Object.fromEntries(orderedFeatures(pkg).map((name) => {
            const f = pkg.features[name];
            return [
                name,
                {
                    name: f.name,
                    states: orderedStates(pkg, name),
                    initial: f.initial,
                    round: f.round,
                    ...(f.onderwerp === true ? { onderwerp: true } : {}),
                    ...(f.briefWoorden === undefined ? {} : { briefWoorden: f.briefWoorden }),
                    ...(f.uiterlijk === undefined ? {} : { uiterlijk: f.uiterlijk }),
                },
            ];
        })),
        picture,
        sight,
        sightComplete: complete,
        ...(measured?.ground === undefined ? {} : { ground: measured.ground }),
        ...(extras.staged
            ? (() => {
                const { scenery, complete } = packageScenery(pkg, measured);
                return { scenery, sceneryComplete: complete };
            })()
            : {}),
        ...(pkg.fog === null || pkg.fog.density <= 0 ? {} : { fog: pkg.fog.density }),
        ...(pkg.houdVrij === undefined || pkg.houdVrij.length === 0
            ? {}
            : { keepClear: pkg.houdVrij.map((s) => ({ azimuth: s.richting, width: s.breedte })) }),
        ...(movers.length === 0 ? {} : { movers }),
        ...(hasAliases(aliases) ? { aliases } : {}),
    };
}
/**
 * The code worlds with the packages: a package in its code world's place (it is what is served), the
 * new ones after, by Dutch name.
 */
export function mergeCatalogue(code, packages) {
    const bySlug = new Map(packages.map((world) => [world.slug, world]));
    const merged = code.map((world) => bySlug.get(world.slug) ?? world);
    const fresh = packages
        .filter((world) => !code.some((c) => c.slug === world.slug))
        .sort((a, b) => a.name.nl.localeCompare(b.name.nl, 'nl'));
    return [...merged, ...fresh];
}
/**
 * The features that are the world's own subject (`onderwerp`, ADR-087), in the catalogue's order:
 * the ones the builder hangs points on. None for a code world or no world.
 */
export function subjectFeatures(world) {
    if (world === undefined)
        return [];
    return Object.entries(world.features)
        .filter(([, feature]) => feature.onderwerp === true)
        .map(([name]) => name);
}
/** The world with this slug, or undefined (also for anything that is not a string). */
export function worldIn(worlds, slug) {
    return typeof slug === 'string' ? worlds.find((world) => world.slug === slug) : undefined;
}
/**
 * The stage a lesson is judged on (ADR-061): its world's own, when the catalogue gives one; for a
 * lesson in no world of it (the studio, a 360° photo), the catalogue's, which is every world's when
 * the generator made it for an older prompt; else `STAGE`.
 */
export function stageOf(lesson, worlds) {
    const all = worlds ?? CODE_WORLD_CATALOGUE;
    if (lesson.scene.type === 'world') {
        const own = worldIn(all, lesson.scene.world)?.stage;
        if (own !== undefined)
            return own;
    }
    return all.find((world) => world.stage !== undefined)?.stage ?? STAGE;
}
/** The lesson's world's features, or none for any other scene or a world the catalogue does not have. */
export function featuresOf(lesson, worlds = CODE_WORLD_CATALOGUE) {
    if (lesson.scene.type !== 'world')
        return {};
    return worldIn(worlds, lesson.scene.world)?.features ?? {};
}
// The names a new world must differ from (nameTaken, NON_WORLD_PLACES) are world-package/names.ts
// (ADR-099), so the kit's `wereld check` runs the same rule from its copy.
export { NON_WORLD_PLACES, nameTaken } from './world-package/names.js';
/** No world: the stage alone, as a catalogue entry only for its stage (a studio lesson takes the catalogue's). */
const NO_WORLD = {
    slug: '',
    name: { nl: '', en: '' },
    description: '',
    teacher: { title: '', text: '' },
    source: 'code',
    staged: false,
    placement: false,
    generatable: false,
    features: {},
    picture: null,
};
/**
 * The worlds an offered place is judged in (ADR-084): `world`, or the stage alone, with the distances an
 * offer keeps (`offerStage`: 6 m from the eyes and outside the clear space), not the 3 m a lesson may come to.
 */
export function offerWorlds(world) {
    const stage = offerStage(world?.stage ?? STAGE);
    return [{ ...(world ?? NO_WORLD), stage }];
}
//# sourceMappingURL=world-catalogue.js.map